import type { AppConfigurations, Customer, PointOfSale, Service } from './types';
import { CustomerState } from '@/Enums';
import { isPlainObject } from '@/scripts/Common/HelperFunctions';

import {
  isPerson,
  isAddress,
  isOptionalNullableBankAccount,
  isBankTransferDetails,
  isOptionalNullableLogo
} from '@/scripts/Common/typeGuards';

import type { PropertyValidator } from '@/scripts/Common/typeValidators';
import {
  hasValidProperties,
  isNumber,
  isString,
  isBoolean,
  isOptionalNullableString,
  isOptionalNullableNumber,
  isOptionalNullableNumberArray,
  createOptionalNullableEnumValidator,
} from '@/scripts/Common/typeValidators';

// ==================== APP CONFIGURATIONS TYPE GUARD ====================

const appConfigurationsValidators: PropertyValidator[] = [
  { key: 'apiKey', required: true, validator: isString },
  { key: 'apiSecret', required: true, validator: isString },
  { key: 'companyName', required: true, validator: isString },
  { key: 'deviceId', required: true, validator: isString },
  { key: 'deviceName', required: true, validator: isString },
  { key: 'serverUri', required: true, validator: isString },
];

export function isAppSetting(value: unknown): value is AppConfigurations {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, appConfigurationsValidators);
}

// ==================== SERVICE TYPE GUARD ====================

const serviceValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'descriptionDe', required: true, validator: isString },
  { key: 'descriptionEn', required: true, validator: isString },
];

export function isService(value: unknown): value is Service {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, serviceValidators);
}

// ==================== CUSTOMER TYPE GUARD ====================

const customerValidators: PropertyValidator[] = [
  // Required properties
  { key: 'id', required: true, validator: isNumber },
  { key: 'contactEmployee', required: true, validator: isPerson },
  { key: 'contactAddress', required: true, validator: isAddress },
  { key: 'businessAddress', required: true, validator: isAddress },
  { key: 'isWebsitePartner', required: true, validator: isBoolean },
  { key: 'useVoucher', required: true, validator: isBoolean },
  { key: 'useEcTerminal', required: true, validator: isBoolean },
  { key: 'isInternetAvailable', required: true, validator: isBoolean },
  { key: 'isSparkassenKunde', required: true, validator: isBoolean },
  { key: 'isEmployer', required: true, validator: isBoolean },
  { key: 'bankTransferDetailsId', required: true, validator: isNumber },
  { key: 'bankTransferDetails', required: true, validator: isBankTransferDetails },

  // Optional properties
  { key: 'companyName', required: false, validator: isOptionalNullableString },
  { key: 'contactEmployeeId', required: false, validator: isOptionalNullableNumber },
  { key: 'taxNumber', required: false, validator: isOptionalNullableString },
  { key: 'vatIdentificationNumber', required: false, validator: isOptionalNullableString },
  { key: 'contactAddressId', required: false, validator: isOptionalNullableNumber },
  { key: 'businessAddressId', required: false, validator: isOptionalNullableNumber },
  { key: 'pointOfSalesCount', required: false, validator: isOptionalNullableNumber },
  { key: 'status', required: false, validator: createOptionalNullableEnumValidator(CustomerState) },
  { key: 'interestedNumber', required: false, validator: isOptionalNullableNumber },
  { key: 'provision', required: false, validator: isOptionalNullableNumber },
  { key: 'businessBankAccountId', required: false, validator: isOptionalNullableNumber },
  { key: 'businessBankAccount', required: false, validator: isOptionalNullableBankAccount },
  { key: 'creditorId', required: false, validator: isOptionalNullableNumber },
  { key: 'websiteUrl', required: false, validator: isOptionalNullableString },
  { key: 'websiteLogo', required: false, validator: isOptionalNullableLogo },
  { key: 'voucherAmount', required: false, validator: isOptionalNullableNumber },
  { key: 'hardware', required: false, validator: isOptionalNullableString },
  { key: 'deploymentDate', required: false, validator: isOptionalNullableString },
  { key: 'category', required: false, validator: isOptionalNullableString },
  { key: 'serviceIds', required: false, validator: isOptionalNullableNumberArray },
  { key: 'bankAccountCreditorId', required: false, validator: isOptionalNullableString },
  { key: 'contractStart', required: false, validator: isOptionalNullableString },
  { key: 'contractEnd', required: false, validator: isOptionalNullableString },
];

export function isCustomer(value: unknown): value is Customer {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, customerValidators);
}

// ==================== POINT OF SALE TYPE GUARD ====================

const pointOfSaleValidators: PropertyValidator[] = [
  { key: 'id', required: true, validator: isNumber },
  { key: 'customerId', required: true, validator: isNumber },
  { key: 'name', required: true, validator: isString },
  { key: 'createdAt', required: true, validator: isString },
  { key: 'editedAt', required: true, validator: isString },
  { key: 'description', required: false, validator: isOptionalNullableString },
];

export function isPointOfSale(value: unknown): value is PointOfSale {
  if (!isPlainObject(value)) return false;
  return hasValidProperties(value, pointOfSaleValidators);
}
